// src/pages/ReaderPageShell.jsx
//
// THIN shared reader-page shell (R1). Responsibilities ONLY: the page background, the
// day/night skin class + font-size class (both from useReadingPrefs), and the compact
// top bar. It renders reader content as `children`.
//
// NOT its job: data fetch, entitlement, audio signed URLs, section/quiz/card rendering,
// reader nav. Visual chrome only -- both readers render inside it unchanged.
//
// lesson-3a-layout: the old prefs band (a right-aligned strip of A/A/A + theme
// controls) and each reader's separate pack-header band are now ONE 48px row -- back
// chevron, pack title, Ask AI, and an overflow menu holding the reading controls. Four
// stacked bands above the content was the single worst thing about the lesson on a
// phone; the controls did not need to be permanently on screen to be findable.
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReadingPrefsContext } from './useReadingPrefs.js';
import { readerShellClasses, READING_SCALES } from './readingPrefs.js';
import { useContentProtection } from './useContentProtection.js';
import ThemeToggle from '../components/ThemeToggle.jsx';
import { IconSparkles } from '../components/icons.jsx';
import '../components/reader/lesson.css';

export default function ReaderPageShell({ packTitle, onAskAi, adminBanner, children }) {
    // The single useReadingPrefs instance now lives at the App root (so the theme also
    // drives non-reader pages + the navbar); we consume it via context here. readingScale
    // stays local to the reader; the theme control is the shared ThemeToggle (context).
    const { colorMode, readingScale, setReadingScale } = useContext(ReadingPrefsContext);
    const shellClass = readerShellClasses({ colorMode, readingScale });
    // Content protection (ADR-009): mounted ONCE here so both readers inherit it. Blocks
    // copy/right-click/shortcuts on .analysis-content; exempts .selectable (AI drawer / Notes).
    const protectToast = useContentProtection();

    return (
        <div className={`page-fade-in min-h-[calc(100vh-4rem)] bg-book-bg paper-texture ${shellClass}`.trim()}>
            <ReaderTopBar
                packTitle={packTitle}
                onAskAi={onAskAi}
                readingScale={readingScale}
                setReadingScale={setReadingScale}
            />
            {/* Admin read-view indicator: a thin line under the bar, admin only. */}
            {adminBanner}
            <div id="reader-tools-slot" className="sticky top-20 z-30 h-0 w-full pointer-events-none" />
            {children}
            {/* Content-protection toast (prototype L14684), shown on a blocked action. */}
            {protectToast && (
                <div className="protect-toast">
                    Content is protected · Take notes using the Notes feature
                </div>
            )}
        </div>
    );
}

// The 'A' glyphs use inline font-size (not Tailwind text-* classes), so reading-scale-*
// never scales the control itself.
const SCALE_GLYPH_PX = { normal: 12, medium: 14, large: 17 };

function ReaderTopBar({ packTitle, onAskAi, readingScale, setReadingScale }) {
    const navigate = useNavigate();
    const [prefsOpen, setPrefsOpen] = useState(false);
    const popRef = useRef(null);

    // Close the popover on an outside click or Escape -- a menu that traps the next tap
    // is worse than no menu on a phone.
    useEffect(() => {
        if (!prefsOpen) return undefined;
        const onDown = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setPrefsOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setPrefsOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [prefsOpen]);

    return (
        <div className="btl-readerbar">
            <div className="max-w-6xl mx-auto px-2 md:px-8 h-full flex items-center gap-1">
                <button type="button" onClick={() => navigate('/dashboard')} aria-label="Back to your library"
                    title="Back to your library"
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full text-book-muted hover:text-book-accent transition-colors">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>

                <span className="min-w-0 flex-grow font-display text-book-text text-sm font-semibold truncate">
                    {packTitle || ''}
                </span>

                <span id="reader-music-slot" className="flex flex-shrink-0" />
                {onAskAi && (
                    <button type="button" onClick={onAskAi} title="Ask the AI Study Partner"
                        className="flex-shrink-0 flex items-center gap-1.5 px-2.5 h-9 rounded-full border border-book-accent/40 text-book-accent hover:bg-book-accent/10 transition-colors font-display text-sm font-medium">
                        <IconSparkles className="w-4 h-4" /> <span className="hidden sm:inline">Ask AI</span>
                    </button>
                )}

                <div className="relative flex-shrink-0" ref={popRef}>
                    <button type="button" onClick={() => setPrefsOpen((o) => !o)}
                        aria-expanded={prefsOpen} aria-haspopup="true"
                        title="Reading options: text size and theme"
                        className="w-10 h-10 flex items-center justify-center rounded-full text-book-muted hover:text-book-accent transition-colors">
                        <span className="font-display font-bold leading-none" style={{ fontSize: 15 }}>Aa</span>
                    </button>
                    {prefsOpen && (
                        <div className="btl-prefs-pop">
                            <div className="font-display text-book-muted text-xs font-semibold mb-1.5">Text size</div>
                            <div className="flex items-center border border-book-accent/25 rounded-full overflow-hidden w-fit mb-3">
                                {READING_SCALES.map((k) => {
                                    const active = readingScale === k;
                                    return (
                                        <button key={k} onClick={() => setReadingScale(k)} title={`Font size: ${k}`}
                                            aria-pressed={active}
                                            className={`px-3 leading-none flex items-center justify-center transition-colors h-9 ${
                                                active ? 'bg-book-accent text-book-pageDark' : 'text-book-muted hover:text-book-accent'
                                            }`}>
                                            <span className="font-display font-bold" style={{ fontSize: SCALE_GLYPH_PX[k] }}>A</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="font-display text-book-muted text-xs font-semibold mb-1.5">Theme</div>
                            <ThemeToggle />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
